import {Navigate, Route, Routes, useNavigate,} from "react-router";
import {Header} from "./component/header/header";
import {Navbar} from "./component/navbar/navbar";
import {JobTracking} from "./views/JobTracking/jobTracking";
import "./App.css";
import {JobList} from "./component/jobList/jobList";
import {Home} from "./component/home/home";
import {UserList} from "./views/Team/userList";
import {Landing} from "./views/Landing/landing";
import {Settings} from "./views/Settings/settings";
import {AccountCreation} from "./views/Admin/AccountCreation/accountCreation";
import {JobWizard} from "./views/Admin/JobCreation/JobWizard";
import {getStoredSession} from "./LocalStorageService";
import {AdminJobList} from "./views/Admin/Job/JobList/jobList";
import {AdminJobManagement} from "./views/Admin/Job/JobManagement/jobManagement";
import {ClientList} from "./views/Admin/ClientList/clientList";
import {AuthProvider, isSigningOut, useAuth} from "./views/AuthProvider/AuthProvider";
import {JobContextProvider} from "./views/JobContextProvider/JobContextProvider";
import {NotFoundElement} from "./NotFoundElement";
import {DataManagement} from "./views/DataManagement/dataManagement";
import {EntraHandoff} from "./views/EntraHandoff/entraHandoff";
// Hidden, not deleted: Microsoft's approval is now the customer's to give, so Tape Ark no longer sends it. See the /MicrosoftSignIn route below.
// import {MicrosoftSignIn} from "./views/Admin/MicrosoftSignIn/microsoftSignIn";

/*
 * Whether to show the signed-in application rather than the sign-in form.
 *
 * The store is read here as each route renders, rather than the auth context, so that a session the axios
 * interceptor has just killed stops admitting the reader on the very next render instead of at the next
 * revalidation. That is worth keeping.
 *
 * What it costs is this: removeStoredSession() runs before the sign-out redirect is asked for, and
 * window.location only *starts* a navigation, so between the two every one of the reads below has already
 * flipped while the document is still on screen and still being painted. Any re-render at all in that window —
 * a notification fetch settling, the job groups publishing — drew <Landing/>, the Tape Ark username and
 * password form, over the top of a session the customer reached from their own portal.
 *
 * So the outbound leg of a sign-out keeps rendering what is already there. Nothing is being admitted that was
 * not admitted a moment ago, and the document it is rendering is on its way to being replaced. See
 * isSigningOut.
 */
const signedIn = () => getStoredSession() || isSigningOut();

function App() {
    const navigate = useNavigate();
    return (
        <div className="App">
            <AuthProvider>
                <JobContextProvider>
                    {signedIn() && <Header/>}
                    <div className="container-fluid">
                        {signedIn() && (
                            <Navbar onViewClicked={(viewSelected) => navigate(viewSelected)}/>
                        )}
                        <div className={`${signedIn() ? "outLet" : ""}`}>
                            <GuardedRoutes/>
                        </div>
                    </div>
                </JobContextProvider>
            </AuthProvider>
        </div>
    );
}

export default App;

const GuardedRoutes = () => {
    const {isFeatureAvailable, user} = useAuth();

    const isCurrentRole = (role) => user?.role === role;

    return (
        <Routes>
            <Route path="/Landing" element={<Landing/>}/>

            {/* Unguarded on purpose, like /Landing: this is what establishes the session, so requiring one
                first would turn every portal sign-in into a bounce to /Landing. See EntraHandoff. */}
            <Route path="/EntraHandoff" element={<EntraHandoff/>}/>
            <Route
                path="/Settings"
                element={signedIn() ? <Settings/> : <Landing/>}/>

            {/*<Route*/}
            {/*  path="/FileServer"*/}
            {/*  element={signedIn() ? <FileServerTable /> : <Landing />} />*/}

            <Route
                path="/"
                element={signedIn() ?
                    ((isFeatureAvailable('dashboard') || isFeatureAvailable('admin_dashboard')) ?
                        <Home/> : <NotFoundElement/>) : <Landing/>}/>

            <Route
                path="/Home"
                element={signedIn() ?
                    ((isFeatureAvailable('dashboard') || isFeatureAvailable('admin_dashboard')) ?
                        <Home/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/AccountCreation"
                element={signedIn() ?
                    (isFeatureAvailable('order') ?
                        <AccountCreation/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/JobCreation"
                element={signedIn() ?
                    (isFeatureAvailable('order') ?
                        <JobWizard/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/Jobs"
                element={signedIn() ?
                    (isFeatureAvailable('order') ?
                        <AdminJobList/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/JobManagement/:orderId"
                element={signedIn() ?
                    (isFeatureAvailable('order') ?
                        <AdminJobManagement/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/Customers"
                element={signedIn() ?
                    (isFeatureAvailable('order') ?
                        <ClientList/> : <NotFoundElement/>) : <Landing/>}/>
            {/*
              * Guarded the same way as /AllUsers rather than as /Customers: this page grants a company's whole
              * directory the right to sign in, which is a Tape Ark administrator's decision and not something a
              * customer-side owner should reach even though they can see their own company record.
              */}
            {/*
              * Hidden, not deleted. Decided with the business: Microsoft's approval is the customer's to give, in
              * their own Microsoft portal or by each person at first sign-in, so Tape Ark no longer emails their
              * administrator. The invitation email and the portal's sign-in screen say what to do when it is missing.
              * Uncomment this, the import above and the two menu entries to bring the page back.
              */}
            {/*
            <Route
                path="/MicrosoftSignIn"
                element={signedIn() ?
                    ((isFeatureAvailable('admin_dashboard') && isCurrentRole("admin")) ?
                        <MicrosoftSignIn/> : <NotFoundElement/>) : <Landing/>}/>
            */}
            <Route
                path="/CustomerJobs"
                element={signedIn() ?
                    ((!isFeatureAvailable('order') && isFeatureAvailable('track')) ?
                        <JobList/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/JobTracking/:orderId"
                element={signedIn() ?
                    ((!isFeatureAvailable('order') && isFeatureAvailable('track')) ?
                        <JobTracking/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/Team"
                element={signedIn() ?
                    ((isFeatureAvailable('user') && !isFeatureAvailable('admin_dashboard') && isCurrentRole("owner")) ?
                        <UserList variant={"CustomerUsers"}/> : <NotFoundElement/>) : <Landing/>}/>
            <Route
                path="/DataManagement"
                element={signedIn() ?
                    ((isFeatureAvailable('restore') || isFeatureAvailable('admin_dashboard')) ?
                        <DataManagement/> : <NotFoundElement/>) : <Landing/>}/>
            <Route path="/Restore" element={signedIn() ? <Navigate to="/DataManagement" replace/> : <Landing/>}/>
            <Route
                path="/AllUsers"
                element={signedIn() ?
                    ((isFeatureAvailable('user') && isFeatureAvailable('admin_dashboard') && isCurrentRole("admin")) ?
                        <UserList variant={"AllUsers"}/> : <NotFoundElement/>) : <Landing/>}/>
            {/*<Route*/}
            {/*    // path="/RestoreTracking/:orderId"*/}
            {/*    path="/RestoreTracking"*/}
            {/*    element={signedIn() ?*/}
            {/*        ((isFeatureAvailable('restore')) ?*/}
            {/*            <RestoreTracking/> : <NotFoundElement/>) : <Landing/>}/>*/}


            {signedIn() && (<>
                <Route path="*"
                       element={
                           <>
                               <div id="main">
                                   <div className="fof">
                                       <h1>You do not have permission to view this page.</h1>
                                   </div>
                               </div>
                           </>
                       }/>
            </>)}
        </Routes>
    );

};
